/**
 * Filter sheet container component — draft filter pattern
 *
 * Uses useFilterPanelLogic for all shared filter logic (draft state,
 * section toggles, chip dropdowns, counts, callbacks). Adds
 * drawer-specific behavior: reinitialize on open transition and
 * click-outside chip close on web.
 * @module features/scouting/components/FilterSheet/FilterSheetContainer
 */

import { useCallback, useEffect, useMemo, useRef } from "react";
import { Platform } from "react-native";

import FilterSheetView from "./FilterSheetView";

import useFilterPanelLogic from "@/features/scouting/hooks/useFilterPanelLogic";
import useListsFilterDraft, {
  type ListsFilterSource,
} from "@/features/scouting/hooks/useListsFilterDraft";
import { toFilterSheetSectionsProps } from "@/features/scouting/utils/toFilterSheetSectionsProps";

import type { FilterState } from "@/hooks/useFilterState";
import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";

/** Props for the FilterSheetContainer */
interface FilterSheetContainerProps {
  readonly discoveryOnly?: boolean;
  readonly sportingOnly?: boolean;
  /**
   * The host's watchlists, to offer the LISTS group (SE-8627). Omitted, the
   * sheet shows no LISTS group — the Watchlist page itself passes none.
   */
  readonly lists?: ListsFilterSource;
  /** Whether the drawer is open */
  readonly isOpen: boolean;
  /** Close the drawer */
  readonly onClose: () => void;
  /** Committed filter state to draft from and commit to */
  readonly filterState: FilterState;
  /** Position filter options */
  readonly positionOptions: readonly FilterOption[];
  /** Club filter options */
  readonly clubOptions: readonly FilterOption[];
  /** Nationality filter options */
  readonly nationalityOptions: readonly FilterOption[];
  /** League filter options */
  readonly leagueOptions: readonly FilterOption[];
  /** Role archetype filter options */
  readonly roleOptions: readonly FilterOption[];
  /** Whether filter option queries are still loading */
  readonly optionsLoading: boolean;
  /** Available org-level custom metrics for filters */
  readonly customMetrics: readonly {
    readonly id: string;
    readonly displayName: string;
  }[];
}

/**
 * Container for the filter sheet — uses useFilterPanelLogic for shared
 * logic and adds drawer-specific open/close behavior.
 * @param root0 - Component props
 * @param root0.sportingOnly - Hide financial controls on sporting-only projections.
 * @param root0.discoveryOnly - Limit the sheet to scenario candidate fields.
 * @param root0.lists - The host's watchlists for the LISTS group (SE-8627).
 * @param root0.isOpen - Whether drawer is open
 * @param root0.onClose - Close drawer callback
 * @param root0.filterState - Committed filter state
 * @param root0.positionOptions - Position filter options
 * @param root0.clubOptions - Club filter options
 * @param root0.nationalityOptions - Nationality filter options
 * @param root0.leagueOptions - League filter options
 * @param root0.roleOptions - Role archetype filter options
 * @param root0.optionsLoading - Whether filter option queries are still loading
 * @param root0.customMetrics - Available org-level custom metrics for filters
 */
const FilterSheetContainer = ({
  discoveryOnly = false,
  sportingOnly = false,
  lists,
  isOpen,
  onClose,
  filterState,
  positionOptions,
  clubOptions,
  nationalityOptions,
  leagueOptions,
  roleOptions,
  optionsLoading,
  customMetrics,
}: FilterSheetContainerProps) => {
  const availableMetricIds = useMemo(
    () => new Set(customMetrics.map(m => m.id)),
    [customMetrics]
  );

  const panelLogic = useFilterPanelLogic({
    filterState,
    onClose,
    availableMetricIds,
  });

  const {
    clearDraft,
    clearSportingDraft,
    reinitialize,
    setOpenChipId,
    openChipId,
    handleShowResults: commitSections,
    activeFilterCount: sectionsActiveCount,
  } = panelLogic;

  const listsDraft = useListsFilterDraft({
    lists,
    isOpen,
    openChipId,
    setOpenChipId,
    loading: optionsLoading,
  });
  const { commit: commitLists, clear: clearLists } = listsDraft;

  /** Commit the LISTS draft alongside the sections, then close. */
  const handleShowResults = useCallback(() => {
    commitLists();
    commitSections();
  }, [commitLists, commitSections]);

  const prevIsOpenRef = useRef(false);

  /** Reset draft filters to defaults */
  const handleClearAll = useCallback(() => {
    clearLists();
    if (sportingOnly) {
      clearSportingDraft();
      return;
    }
    clearDraft();
  }, [clearDraft, clearLists, clearSportingDraft, sportingOnly]);

  /* --- Drawer-specific side effects --- */

  /** Re-snapshot committed state on open, close chip dropdowns on close */
  useEffect(() => {
    const justOpened = isOpen && !prevIsOpenRef.current;
    const justClosed = !isOpen && prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;
    if (justOpened) {
      reinitialize();
      setOpenChipId(null);
    }
    if (justClosed) {
      setOpenChipId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only react to isOpen transitions
  }, [isOpen]);

  /** Close chip dropdown when clicking outside on web */
  useEffect(() => {
    if (Platform.OS !== "web" || !openChipId) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      /** Check target and all ancestors for the dropdown attribute */
      const isInsideChipDropdown = target.closest?.("[data-chip-dropdown]");
      /** Also check if any portal-rendered dropdown contains the target */
      const portalDropdowns = document.querySelectorAll("[data-chip-dropdown]");
      const isInsidePortalDropdown = Array.from(portalDropdowns).some(
        dropdown => dropdown.contains(target)
      );
      if (!isInsideChipDropdown && !isInsidePortalDropdown) {
        setOpenChipId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openChipId, setOpenChipId]);

  return (
    <FilterSheetView
      {...toFilterSheetSectionsProps(panelLogic, {
        discoveryOnly,
        sportingOnly,
        positionOptions,
        clubOptions,
        nationalityOptions,
        leagueOptions,
        roleOptions,
        optionsLoading,
        onServerSearchClub: discoveryOnly
          ? undefined
          : filterState.setClubSearchText,
        clubSearching: discoveryOnly ? false : filterState.clubSearching,
        customMetrics,
      })}
      lists={listsDraft.section}
      isOpen={isOpen}
      onClose={onClose}
      onShowResults={handleShowResults}
      onCancel={panelLogic.handleCancel}
      onClearAll={handleClearAll}
      activeFilterCount={sectionsActiveCount + listsDraft.draftCount}
    />
  );
};

export default FilterSheetContainer;
